/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { ExternalLink } from 'lucide-react';
import type { StoryAcceptanceSummary } from '@shared/types/acceptance-reports';

interface StorySummaryRowProps {
  story: StoryAcceptanceSummary;
  onStoryClick?: (storyId: string, epicId: string) => void;
}

const STATUS_COLORS = {
  accepted: '#10b981',
  readyForReview: '#f59e0b',
  scoped: '#8b5cf6',
  required: '#6b7280',
};

export function StorySummaryRow({ story, onStoryClick }: StorySummaryRowProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStoryClick) {
      onStoryClick(story.storyId, story.epicId);
    } else {
      window.location.href = `/stories?epicId=${encodeURIComponent(story.epicId)}&storyId=${encodeURIComponent(story.storyId)}`;
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr repeat(4, 60px) 32px',
        alignItems: 'center',
        padding: '8px 12px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: '#fafafa',
        fontSize: '13px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#0066cc',
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {story.storyId}
        </span>
        <span
          style={{
            color: '#444',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={story.title}
        >
          {story.title}
        </span>
      </div>

      <div
        style={{
          textAlign: 'center',
          fontWeight: story.accepted > 0 ? 600 : 400,
          color: story.accepted > 0 ? STATUS_COLORS.accepted : '#ccc',
        }}
        title="Accepted"
      >
        {story.accepted}
      </div>

      <div
        style={{
          textAlign: 'center',
          fontWeight: story.readyForReview > 0 ? 600 : 400,
          color: story.readyForReview > 0 ? STATUS_COLORS.readyForReview : '#ccc',
        }}
        title="Ready For Review"
      >
        {story.readyForReview}
      </div>

      <div
        style={{
          textAlign: 'center',
          fontWeight: story.scoped > 0 ? 600 : 400,
          color: story.scoped > 0 ? STATUS_COLORS.scoped : '#ccc',
        }}
        title="Scoped"
      >
        {story.scoped}
      </div>

      <div
        style={{
          textAlign: 'center',
          fontWeight: story.required > 0 ? 600 : 400,
          color: story.required > 0 ? STATUS_COLORS.required : '#ccc',
        }}
        title="Required"
      >
        {story.required}
      </div>

      <button
        onClick={handleClick}
        style={{
          padding: '4px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#666',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
        }}
        title="View story details"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#e5e7eb';
          e.currentTarget.style.color = '#0066cc';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#666';
        }}
      >
        <ExternalLink size={14} />
      </button>
    </div>
  );
}
